import * as React from 'react';
import {
  root as rootCls,
  track as trackCls,
  fill as fillCls,
  mark as markCls,
} from './ProgressBar.css';

export type ProgressBarSize = 'sm' | 'md' | 'lg';

export interface ProgressBarProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'aria-valuenow' | 'aria-valuemin' | 'aria-valuemax'
> {
  size?: ProgressBarSize;
  /** Percent complete, 0–100 (clamped). */
  value: number;
  /**
   * Reference ticks at these percents (e.g. budget thresholds 70/85/100).
   * Decorative only — `aria-hidden`; say what they mean in nearby text.
   */
  marks?: readonly number[];
  /** Accessible name for the progress element (e.g. the filename being uploaded). */
  'aria-label': string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLDivElement>;
}

/**
 * Determinate progress bar (Drop 8 / Phase G — the upload flow's S3-POST
 * indicator). `role="progressbar"` + the standard `aria-value*` triad; the
 * fill's `width` transition is the DS `transition()` mixin, which the global
 * `prefers-reduced-motion` override already collapses to instant (DS a11y
 * baseline — no separate reduced-motion branch needed here).
 */
export function ProgressBar({
  size = 'md',
  value,
  marks,
  ref,
  className,
  ...rest
}: ProgressBarProps) {
  const pct = Math.min(100, Math.max(0, value));
  const cls = [rootCls({ size }), className].filter(Boolean).join(' ');
  return (
    <div
      ref={ref}
      className={cls}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      {...rest}
    >
      <div className={trackCls}>
        <div className={fillCls} style={{ width: `${pct}%` }} />
      </div>
      {marks?.map((m) => (
        <span
          key={m}
          aria-hidden="true"
          data-mark={m}
          className={markCls}
          style={{ left: `${Math.min(100, Math.max(0, m))}%` }}
        />
      ))}
    </div>
  );
}
ProgressBar.displayName = 'ProgressBar';
